import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Select, Space, Table, Tag, message } from 'antd';
import {
  AcceptanceChangeRequest,
  acceptanceChangeRequestApi,
} from '../../services/acceptanceChangeRequestApi';
import { AcceptanceManifest } from './types';
import { useAcceptanceMode } from './AcceptanceModeContext';

const nextStates: Record<string, string[]> = {
  submitted: ['triaged', 'rejected'],
  triaged: ['needs_business_evidence', 'owner_approved', 'rejected'],
  needs_business_evidence: ['triaged', 'owner_approved', 'rejected'],
  owner_approved: ['contract_updated'],
  contract_updated: ['verifier_red_proven'],
  verifier_red_proven: ['implementation_ready'],
  implementation_ready: ['verified_green'],
  verified_green: ['user_reaccepted'],
  user_reaccepted: ['closed'],
};

export const AcceptanceChangeRequestList: React.FC<{ manifest: AcceptanceManifest }> = ({ manifest }) => {
  const { canManageGlobal } = useAcceptanceMode();
  const [requests, setRequests] = useState<AcceptanceChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await acceptanceChangeRequestApi.list(manifest.formId));
    } catch {
      message.error('讀取 Contract Change Request 失敗');
    } finally {
      setLoading(false);
    }
  }, [manifest.formId]);

  useEffect(() => { void load(); }, [load]);

  const transition = async (request: AcceptanceChangeRequest, status: string) => {
    try {
      await acceptanceChangeRequestApi.transition(request.requestId, status);
      message.success(`提案 #${request.requestId} 已移至 ${status}`);
      await load();
    } catch {
      message.error('狀態轉換失敗；伺服器會拒絕跳過必要階段');
    }
  };

  return <>
    <Alert
      type="info"
      showIcon
      message="提案只記錄期望與證據上下文，不會直接修改 Contract、Git 或 ERP 業務資料。"
      style={{ marginBottom: 12 }}
    />
    <Table
      rowKey="requestId"
      loading={loading}
      dataSource={requests}
      pagination={{ pageSize: 10 }}
      columns={[
        { title: '#', dataIndex: 'requestId', width: 70 },
        { title: '範圍', key: 'context', render: (_: unknown, row: AcceptanceChangeRequest) => `${row.contextType}｜${row.fieldId || row.controlId || row.formId}` },
        { title: '期望行為', dataIndex: 'expectedBehavior' },
        { title: 'Contract', dataIndex: 'contractHash', render: (hash: string) => hash?.slice(0, 12) },
        { title: '狀態', dataIndex: 'status', render: (status: string) => <Tag color={status === 'closed' ? 'green' : status === 'rejected' ? 'red' : 'blue'}>{status}</Tag> },
        { title: '提出人', dataIndex: 'submittedBy' },
        {
          title: '管理者裁決',
          key: 'transition',
          render: (_: unknown, row: AcceptanceChangeRequest) => canManageGlobal && nextStates[row.status]?.length
            ? <Space>
              <Select
                style={{ width: 220 }}
                placeholder="選擇下一狀態"
                options={nextStates[row.status].map(value => ({ value, label: value }))}
                onChange={value => { void transition(row, value); }}
              />
            </Space>
            : <span>—</span>,
        },
      ]}
      scroll={{ x: 1100 }}
    />
    <Button onClick={() => { void load(); }}>重新讀取提案</Button>
  </>;
};
